/**
 * N2Frame — shared page wrapper for every /n2/* route.
 *
 * Responsibilities:
 *   - Load Fraunces (production display face) via a scoped <link>. Only added
 *     to <head> when a /n2 page is rendered — no font-load tax on app pages.
 *   - Inject the Notches palette + type tokens as CSS custom properties
 *     scoped to the .n2-root element. Nothing leaks into the app.
 *   - Render the light-cream site chrome: brand mark top-left, minimal nav.
 *   - Fires the existing tracker beacon so we can attribute traffic on /n2
 *     pages the same way as the study pages.
 *
 * Committed light-theme; the marketing site does not follow the OS dark
 * preference. See brand/positioning.md — the book is not a dark object.
 */

import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cssVariables, palette } from './theme';

const BEACON_URL = 'https://icq67h6a7f.execute-api.us-east-2.amazonaws.com/track';

function beacon(page: string) {
  try {
    const source = new URLSearchParams(window.location.search).get('utm_source') || 'organic';
    fetch(BEACON_URL, {
      method: 'POST', mode: 'cors', keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page, source, referer: document.referrer || '' }),
    }).catch(() => {});
  } catch { /* never block render */ }
}

// Fraunces is added to <head> the first time any /n2 page mounts. Idempotent.
function ensureFraunces() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('n2-fraunces-font')) return;
  const link = document.createElement('link');
  link.id = 'n2-fraunces-font';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..700;1,9..144,300..700&display=swap';
  document.head.appendChild(link);
}

interface N2FrameProps {
  children: React.ReactNode;
  /** Short page name for the beacon. e.g. 'landing', 'scorecard'. */
  page: string;
}

export const N2Frame: React.FC<N2FrameProps> = ({ children, page }) => {
  useEffect(() => { ensureFraunces(); }, []);
  useEffect(() => { beacon(`n2/${page}`); }, [page]);

  return (
    <div
      className="n2-root"
      style={{
        // Cast so React accepts custom properties in the style object.
        ...(Object.fromEntries(
          cssVariables
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean)
            .map((l) => {
              const m = l.match(/^(--[a-z0-9-]+):\s*(.+?);?$/);
              return m ? [m[1], m[2]] : null;
            })
            .filter(Boolean) as [string, string][]
        )) as React.CSSProperties,
        background: palette.paper,
        color: palette.ink,
        minHeight: '100vh',
        fontFamily: 'var(--sans)',
        // Reset CRA App.css's global `text-align: center` — it leaks into
        // markdown bodies where paragraph <p> / <li> inherit alignment.
        // Anything that wants centering opts in via inline textAlign.
        textAlign: 'left',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }}
    >
      <N2Header />
      <main>{children}</main>
      <N2Footer />
    </div>
  );
};

const N2Header: React.FC = () => {
  const loc = useLocation();
  const { user } = useAuth();
  // Login/register links carry `?redirect=<current-path>` so returning
  // users land back on the page they came from. Login.tsx prepends '/'
  // to the redirect value, so we must strip the leading slash here to
  // avoid producing `//n2/...` which the browser reads as a protocol-
  // relative URL to host "n2".
  const authRedirect = encodeURIComponent((loc.pathname + loc.search).replace(/^\/+/, ''));
  return (
    <header style={{
      borderBottom: `1px solid ${palette.line}`,
      background: palette.paper,
    }}>
      <div style={{
        maxWidth: 1100, margin: '0 auto', padding: '20px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 20, flexWrap: 'wrap',
      }}>
        <Link
          to="/n2"
          style={{
            display: 'flex', alignItems: 'baseline', gap: 10,
            textDecoration: 'none', color: palette.ink,
          }}
        >
          <span style={{
            fontFamily: 'var(--serif)', fontSize: 24, fontWeight: 400,
            letterSpacing: '-0.01em',
          }}>Notches</span>
          <span style={{
            fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: palette.ink3,
          }}>by WFS</span>
        </Link>

        <nav style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
          <N2NavLink to="/n2/scorecard" label="Scorecard" active={loc.pathname.startsWith('/n2/scorecard')} />
          <N2NavLink to="/n2/briefings" label="Briefings" active={loc.pathname.startsWith('/n2/briefings')} />
          <N2NavLink to="/n2/research" label="Research" active={loc.pathname.startsWith('/n2/research')} />
          <N2NavLink to="/n2/methodology" label="Methodology" active={loc.pathname === '/n2/methodology'} />
          <N2NavLink to="/n2/about" label="About" active={loc.pathname === '/n2/about'} />
          {user?.calendarNotchesEnabled && (
            <N2NavLink to="/n2/calendar-notches" label="Calendar Read" active={loc.pathname === '/n2/calendar-notches'} />
          )}
          {user ? (
            <Link to="/notches/dashboard" style={n2AuthLinkStyle(true)}>Dashboard →</Link>
          ) : (
            <Link to={`/login?redirect=${authRedirect}`} style={n2AuthLinkStyle(false)}>Sign in →</Link>
          )}
        </nav>
      </div>
    </header>
  );
};

function n2AuthLinkStyle(filled: boolean): React.CSSProperties {
  return filled
    ? {
        fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600,
        color: palette.paper, background: palette.ink,
        padding: '6px 14px', borderRadius: 4, textDecoration: 'none',
      }
    : {
        fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600,
        color: palette.ink, background: 'transparent',
        border: `1px solid ${palette.ink}`, padding: '5px 13px',
        borderRadius: 4, textDecoration: 'none',
      };
}

const N2NavLink: React.FC<{ to: string; label: string; active: boolean }> = ({ to, label, active }) => (
  <Link
    to={to}
    style={{
      fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 500,
      color: active ? palette.ink : palette.ink2,
      textDecoration: 'none',
      borderBottom: active ? `1.5px solid ${palette.gold}` : '1.5px solid transparent',
      paddingBottom: 2,
    }}
  >
    {label}
  </Link>
);

const N2Footer: React.FC = () => (
  <footer style={{
    borderTop: `1px solid ${palette.line}`, background: palette.paper,
    marginTop: 96, padding: '32px 32px 40px',
  }}>
    <div style={{
      maxWidth: 1100, margin: '0 auto',
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      flexWrap: 'wrap', gap: 16,
    }}>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3 }}>
        Notches is a product of <strong style={{ color: palette.ink2, fontWeight: 600 }}>WFS · Wealth Financial Systems</strong>.
      </div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3, letterSpacing: '0.06em' }}>
        Not investment advice. Not a tip service. See our{' '}
        <Link to="/n2/methodology" style={{ color: palette.goldDeep }}>methodology</Link>.
      </div>
    </div>
  </footer>
);
